<template>
	<div
		ref="wrapper"
		class="error-screen"
		:style="wrapperStyle"
	>
		<div
			class="error-screen__inner"
			:style="innerStyle"
		>
			<!-- event information -->
			<div
				v-if="info"
				class="error-screen__event-information"
			>
				<div class="error-screen__event-information__title">
					<a
						:href="info.url"
						target="_blank"
					>
						<span>{{ info.name }}</span>
					</a>
				</div>
				<div class="error-screen__event-information__subtitle">
					<span v-if="info.eventContext">
						<span class="error-screen__event-information__subtitle__context">
							{{ info.eventContext }}
						</span>
						&bull;
					</span>
					<span v-if="info.parentCategory"> {{ info.parentCategory }} &bull;</span>
					<span v-if="info.childCategory"> {{ info.childCategory }} &bull;</span>
					<span v-if="info.eventDateTime"> {{ info.eventDateTime }} </span>
					<span v-if="info.location"> &bull; {{ info.location }} </span>
				</div>
			</div>
			<!-- error -->
			<div class="error-screen__error">
				<Icon
					icon-name="c-info"
					size="xxxl"
				/>
				<div
					class="error-screen__error__title"
					v-html="screen.title"
				/>
				<div
					v-if="screen.subtitle"
					class="error-screen__error__subtitle"
				>
					{{ screen.subtitle }}
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import Icon from '@components/Icon';
import ErrorScreen from '../../logic/error-screen';

export default {
	name:       'ErrorScreen',
	components: { Icon },
	props:      {
		screen: {
			type:     ErrorScreen,
			required: true,
		},
		info: {
			type:    Object,
			default: null,
		},
	},
	data() {
		return {
			wrapperWidth:  1920,
			wrapperHeight: 1080,
		};
	},
	computed: {
		wrapperStyle() {
			const height = (this.wrapperWidth * 1080) / 1920;
			return {
				height: `${height}px`,
			};
		},
		innerStyle() {
			const scale = (this.wrapperWidth * 100) / 1920 / 100;

			return {
				width:     '1920px',
				height:    '1080px',
				transform: `translate(-50%, -50%) scale(${scale})`,
			};
		},
	},
	mounted() {
		if (!this.$refs.wrapper) throw new Error('Error screen unable to find $refs.wrapper.');
		if (window.ResizeObserver) {
			this.resizeObserver = new ResizeObserver(() => {
				this.updateWrapperSize();
			});
			this.resizeObserver.observe(this.$refs.wrapper);
		}
		this.updateWrapperSize();
	},
	destroyed() {
		if (this.$refs.wrapper && this.resizeObserver) this.resizeObserver.unobserve(this.$refs.wrapper);
	},
	methods: {
		updateWrapperSize() {
			if (!this.$refs.wrapper) return;
			const rect = this.$refs.wrapper.getBoundingClientRect();
			this.wrapperWidth = rect.width;
			this.wrapperHeight = rect.height;
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

$as-error-screen-background: var(--color-grey-900);
$as-error-screen-foreground: var(--color-soft-white);
$as-error-screen-subtitle-context-foreground: var(--color-hype-yellow);
$as-error-screen-info-background: var(--color-grey-850);
$as-error-screen-error-subtitle-foreground: var(--color-grey-300);

.error-screen {
	position: relative;
	width: 100%;
	background-color: $as-error-screen-background;
	color: $as-error-screen-foreground;

	body.-t-light & {
		background-color: var(--color-grey-100);
	}

	&__inner {
		position: absolute;
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		top: 50%;
		left: 50%;
		padding: 86px 88px;
		width: 1920px;
		height: 1080px;
	}

	&__event-information {
		background: $as-error-screen-info-background;
		padding: 24px;

		body.-t-light & {
			background-color: var(--color-grey-300);
		}

		&__title {
			a {
				color: $as-error-screen-foreground;
				font-size: 32px;
				font-weight: 300;
				line-height: 40px;
			}
		}

		&__subtitle {
			font-size: 14px;
			line-height: 20px;
			margin-top: 8px;

			&__context {
				color: $as-error-screen-subtitle-context-foreground;
			}
		}
	}

	&__error {
		display: flex;
		flex-direction: column;

		&__title, &__title::v-deep a {
			margin-top: 42px;
			font-size: 48px;
			font-weight: 300px;
			line-height: 56px;
		}

		&__title::v-deep a {
			text-decoration: underline;
			color: $as-error-screen-foreground;
		}

		&__subtitle {
			margin-top: 16px;
			font-size: 32px;
			font-weight: 300px;
			line-height: 40px;
			color: $as-error-screen-error-subtitle-foreground;

			body.-t-light & {
				color: var(--color-grey-700);
			}
		}
	}
}
</style>
